import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2, Mail, ArrowLeft, CheckCircle2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      toast.error('Please enter a valid email');
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Something went wrong. Try again.');
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <Card className="w-full max-w-md">
        <CardContent className="p-8">
          <button onClick={() => navigate('/login')} className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1 mb-4" data-testid="back-to-login">
            <ArrowLeft className="w-3 h-3" /> Back to login
          </button>

          {!sent ? (
            <>
              <div className="flex items-center gap-2 mb-4">
                <Mail className="w-6 h-6 text-blue-600" />
                <h1 className="text-xl font-semibold">Forgot Password</h1>
              </div>
              <p className="text-sm text-slate-600 mb-5">
                Enter your account email. We&apos;ll send you a link to reset your password. Link expires in 1 hour.
              </p>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com" required data-testid="forgot-email-input" />
                </div>
                <Button type="submit" disabled={busy} className="w-full" data-testid="forgot-submit-btn">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Send Reset Link
                </Button>
              </form>
            </>
          ) : (
            <div className="text-center py-6" data-testid="forgot-success">
              <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
              <h2 className="text-lg font-semibold mb-2">Check your inbox</h2>
              <p className="text-sm text-slate-600">
                If an account exists with <b>{email}</b>, a reset link is on its way. The link expires in 1 hour.
              </p>
              <p className="text-xs text-slate-500 mt-4">
                Didn&apos;t receive it? Check spam. You can request another link in 1 hour.
              </p>
              <Link to="/login" className="inline-block mt-6 text-sm text-blue-600 hover:underline">
                Return to login
              </Link>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
